/**
 * OmniLeagues — Team Standings & Statistics
 *
 * Two views stacked:
 *   1. Current season standings (live OL/MSTW) — preserved from original UI.
 *   2. Historical breakdown — uses `season_archives` as the OL statistics SoT.
 *      Has a Sport filter + Season selector + per-team inline-editable cards.
 *      Every numeric edit autosaves; AD-edited values become canonical and
 *      future MSTW pulls will respect `edited_by_ad: true`.
 *
 * The historical view mirrors the public site's archive page format
 * (total record card → split row → per-team cards) so an AD's mental
 * model is identical whether they're auditing here or viewing the site.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Trophy, Users, TrendingUp, Minus, TrendingDown, Save, BarChart3,
  Loader2, Plus, Globe, History, CheckCircle2, AlertCircle, Send,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOKEN_LABEL = {
  VB: 'Varsity Boys',  VG: 'Varsity Girls',
  JVB: 'JV Boys',      JVG: 'JV Girls',
  MSB: 'Middle School Boys', MSG: 'Middle School Girls',
};
const TOKEN_ORDER = ['VB', 'VG', 'JVB', 'JVG', 'MSB', 'MSG'];
const SPLIT_KEYS = ['homeschool', 'private', 'nationals'];
const SPLIT_LABEL = { homeschool: 'Homeschool', private: 'Private', nationals: 'Nationals' };

function fmtRecord(rec) {
  const w = Number(rec?.w ?? 0); const l = Number(rec?.l ?? 0);
  return `${w}-${l}`;
}
function pct(rec) {
  const w = Number(rec?.w ?? 0); const l = Number(rec?.l ?? 0);
  const gp = w + l;
  return gp ? `${(100 * w / gp).toFixed(1)}%` : '—';
}

export default function StatsPanel({ orgId, teams, userRole }) {
  const { isDark } = useTheme();
  const canEdit = ['ad', 'company_support'].includes(userRole);

  // ── Current season standings (existing behaviour) ─────────────────────
  const [standings, setStandings] = useState([]);
  const [dataSource, setDataSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [games, setGames] = useState([]);
  const [scoreForm, setScoreForm] = useState({ home_score: '', away_score: '' });
  const [saving, setSaving] = useState(false);
  // Standings sport/season selectors (options resolved from the backend).
  const [stdSport, setStdSport] = useState('');
  const [stdSeason, setStdSeason] = useState('');
  const [stdSports, setStdSports] = useState([]);
  const [stdSeasons, setStdSeasons] = useState([]);

  // ── Historical archive view ───────────────────────────────────────────
  const [archivesIndex, setArchivesIndex] = useState({ by_sport: {} });
  const [activeSport, setActiveSport] = useState('basketball');
  const [activeSeason, setActiveSeason] = useState(null);
  const [archiveDoc, setArchiveDoc] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);
  // Archive Sync Mirror — Session 25 r1.
  const [pushingArchive, setPushingArchive] = useState(false);
  const [archivePushMsg, setArchivePushMsg] = useState(null);
  const [archMsg, setArchMsg] = useState(null);

  // Autosave plumbing for inline edits
  const saveTimers = useRef({});
  const [rowState, setRowState] = useState({}); // { rowKey: 'saving'|'saved'|'error' }

  const flashArchMsg = (type, text) => {
    setArchMsg({ type, text });
    setTimeout(() => setArchMsg(null), 4000);
  };

  // ── Loaders ───────────────────────────────────────────────────────────
  const fetchStandings = useCallback(async (sp, se) => {
    try {
      const params = new URLSearchParams();
      if (sp) params.set('sport', sp);
      if (se) params.set('season', se);
      const qs = params.toString();
      const res = await axios.get(`${API_URL}/api/stats/org/${orgId}/standings${qs ? `?${qs}` : ''}`);
      setStandings(res.data.standings || []);
      setDataSource(res.data.source || 'ol');
      if (res.data.sports) setStdSports(res.data.sports);
      if (res.data.seasons) setStdSeasons(res.data.seasons);
      if (res.data.sport) setStdSport(res.data.sport);
      if (res.data.season) setStdSeason(res.data.season);
    } catch { setStandings([]); }
    setLoading(false);
  }, [orgId]);

  const fetchGames = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scheduling/games/${orgId}`);
      setGames(res.data || []);
    } catch { setGames([]); }
  }, [orgId]);

  const fetchArchivesIndex = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await axios.get(`${API_URL}/api/stats/archives/${orgId}`);
      setArchivesIndex(res.data || { by_sport: {} });
      // Default sport+season selection — Basketball first if it has data
      const by = res.data?.by_sport || {};
      const firstSport = (by.basketball && by.basketball.length) ? 'basketball'
        : Object.keys(by).find(k => (by[k] || []).length > 0)
        || 'basketball';
      setActiveSport(firstSport);
      const seasons = by[firstSport] || [];
      if (seasons.length) setActiveSeason(seasons[0].season_slug);
    } catch (e) { setArchivesIndex({ by_sport: {} }); }
  }, [orgId]);

  const fetchArchiveDoc = useCallback(async (sport, season) => {
    if (!orgId || !sport || !season) { setArchiveDoc(null); return; }
    setArchiveLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/stats/archives/${orgId}/${sport}/${season}`);
      setArchiveDoc(res.data);
    } catch (e) {
      setArchiveDoc(null);
    } finally {
      setArchiveLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchStandings(); fetchGames(); fetchArchivesIndex(); }, [fetchStandings, fetchGames, fetchArchivesIndex]);
  useEffect(() => { if (activeSport && activeSeason) fetchArchiveDoc(activeSport, activeSeason); }, [activeSport, activeSeason, fetchArchiveDoc]);

  // ── Autosave one team's partial patch ────────────────────────────────
  const setRowSave = (key, state) => {
    setRowState(s => ({ ...s, [key]: state }));
    if (state === 'saved') {
      setTimeout(() => setRowState(s => (s[key] === 'saved' ? { ...s, [key]: 'idle' } : s)), 1500);
    }
  };

  const saveTeamPatch = useCallback(async (token, patch) => {
    if (!activeSport || !activeSeason) return;
    const key = `${token}`;
    setRowSave(key, 'saving');
    try {
      const res = await axios.patch(
        `${API_URL}/api/stats/archives/${orgId}/${activeSport}/${activeSeason}`,
        { teams: { [token]: patch } },
      );
      setArchiveDoc(res.data);
      setRowSave(key, 'saved');
    } catch (e) {
      setRowSave(key, 'error');
      flashArchMsg('err', e.response?.data?.detail || 'Save failed');
    }
  }, [activeSport, activeSeason, orgId]);

  // Debounced wrapper — every keystroke schedules a save 800ms after the user stops typing.
  const scheduleTeamPatch = (token, patch) => {
    const key = `${token}:${JSON.stringify(Object.keys(patch))}`;
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    setRowSave(token, 'saving');
    saveTimers.current[key] = setTimeout(() => saveTeamPatch(token, patch), 800);
  };

  // Optimistic in-memory mutation (so the UI updates instantly while debounced save flies)
  const mutateTeam = (token, mutator) => {
    setArchiveDoc(prev => {
      if (!prev) return prev;
      const teams = { ...(prev.teams || {}) };
      teams[token] = mutator({ ...(teams[token] || {}) });
      return { ...prev, teams };
    });
  };

  // Push this archive's underlying games back to WordPress.
  const pushArchiveToWp = async () => {
    if (!activeSport || !activeSeason) return;
    if (!window.confirm(`Push corrected ${activeSport} / ${activeSeason} stats back to WordPress?\n\nThis re-emits every game for the season so the WP public archive matches your OL edits.`)) return;
    setPushingArchive(true);
    setArchivePushMsg(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/season-lifecycle/archives/${orgId}/${activeSport}/${activeSeason}/push-to-wp`
      );
      setArchivePushMsg({ type: 'ok', text: `Queued ${data.pushed_games} game(s) for re-publish to WordPress.` });
    } catch (err) {
      setArchivePushMsg({ type: 'err', text: err.response?.data?.detail || 'Push failed' });
    } finally {
      setPushingArchive(false);
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────
  const bg = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const inputCls = isDark ? 'bg-slate-900 border-slate-600 text-white' : 'bg-white border-slate-300';
  const canRecord = ['ad', 'coach', 'company_support'].includes(userRole);
  const acceptedGames = games.filter(g => g.status === 'accepted');

  const sportOptions = useMemo(() => {
    // Always include the canonical 3 sports — backend auto-seeds them.
    const canonical = [
      { slug: 'basketball', label: 'Basketball' },
      { slug: 'baseball',   label: 'Baseball'   },
      { slug: 'volleyball', label: 'Volleyball' },
    ];
    return canonical.map(s => ({
      ...s,
      hasArchives: ((archivesIndex.by_sport || {})[s.slug] || []).length > 0,
    }));
  }, [archivesIndex]);

  const seasonsForActiveSport = archivesIndex.by_sport?.[activeSport] || [];

  if (loading) return <div className="flex justify-center py-12" data-testid="stats-loading"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-8" data-testid="stats-panel">
      {/* ── HEADER ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-blue-500" />
          <h2 className={`text-lg font-semibold ${text}`}>Team Standings &amp; Statistics</h2>
          {dataSource === 'mstw' && (
            <Badge className="bg-blue-500/20 text-blue-400 ml-1 flex items-center gap-1">
              <Globe className="h-3 w-3" />MSTW Live
            </Badge>
          )}
        </div>
        {canRecord && acceptedGames.length > 0 && (
          <Button size="sm" onClick={() => setShowRecordDialog(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="record-result-btn">
            <Plus className="h-4 w-4 mr-1" /> Record Result
          </Button>
        )}
      </div>

      {/* ── SEASON STANDINGS (computed from the games SOT) ────────────── */}
      <Card className={bg} data-testid="season-standings-card">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className={`text-sm font-medium ${muted}`}>Season Standings</CardTitle>
            <div className="flex items-center gap-2">
              <select
                data-testid="standings-sport-select"
                value={stdSport}
                onChange={(e) => { const v = e.target.value; setStdSport(v); fetchStandings(v, stdSeason); }}
                className={`text-xs rounded-md border px-2 py-1 ${inputCls}`}>
                {stdSports.map((s) => (
                  <option key={s.id} value={s.name}>{s.name}</option>
                ))}
              </select>
              <select
                data-testid="standings-season-select"
                value={stdSeason}
                onChange={(e) => { const v = e.target.value; setStdSeason(v); fetchStandings(stdSport, v); }}
                className={`text-xs rounded-md border px-2 py-1 ${inputCls}`}>
                {stdSeasons.map((s) => (
                  <option key={s.slug} value={s.slug}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {standings.length === 0 ? (
            <div className="py-8 text-center" data-testid="standings-empty">
              <Trophy className={`h-10 w-10 mx-auto mb-3 ${muted}`} />
              <p className={muted}>No games recorded for {stdSport || 'this sport'} {stdSeason || ''} yet — standings build automatically as results land.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="standings-table">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    <th className={`text-left py-2 px-2 font-medium ${muted}`}>#</th>
                    <th className={`text-left py-2 px-2 font-medium ${muted}`}>Team</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>W</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>L</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>T</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>GP</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Win%</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>PF</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>PA</th>
                    <th className={`text-center py-2 px-1 font-medium ${muted}`}>Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((s, idx) => (
                    <tr key={s.team_id} className={`border-b ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}>
                      <td className={`py-2 px-2 ${muted}`}>{idx + 1}</td>
                      <td className={`py-2 px-2 font-medium ${text}`}>{s.team_name}</td>
                      <td className="py-2 px-1 text-center text-green-500 font-medium">{s.wins}</td>
                      <td className="py-2 px-1 text-center text-red-400 font-medium">{s.losses}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{s.ties}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{s.games_played}</td>
                      <td className={`py-2 px-1 text-center font-medium ${text}`}>{s.win_pct}%</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{s.points_for}</td>
                      <td className={`py-2 px-1 text-center ${muted}`}>{s.points_against}</td>
                      <td className="py-2 px-1 text-center">
                        <span className={`inline-flex items-center gap-0.5 ${(s.points_for - s.points_against) > 0 ? 'text-green-500' : (s.points_for - s.points_against) < 0 ? 'text-red-400' : muted}`}>
                          {(s.points_for - s.points_against) > 0 ? <TrendingUp className="h-3 w-3" /> : (s.points_for - s.points_against) < 0 ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                          {s.points_for - s.points_against > 0 ? '+' : ''}{s.points_for - s.points_against}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── HISTORICAL BREAKDOWN ─────────────────────────────────────── */}
      <Card className={bg} data-testid="historical-stats-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-amber-500" />
              <CardTitle className={`text-sm font-medium ${text}`}>Historical Stats by Season</CardTitle>
              <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-400 bg-amber-500/10">
                OL statistics dataset · AD edits become SoT
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Flash message */}
          {archMsg && (
            <div className={`flex items-start gap-2 text-xs p-2.5 rounded-md ${archMsg.type === 'ok' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : archMsg.type === 'warn' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
              {archMsg.type === 'ok' ? <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />}
              {archMsg.text}
            </div>
          )}

          {/* Sport + Season dropdowns */}
          <div className="flex flex-wrap items-end gap-4" data-testid="historical-filters">
            <div className="flex flex-col gap-1">
              <span className={`text-[10px] uppercase tracking-wide font-semibold ${muted}`}>Sport</span>
              <select value={activeSport}
                onChange={e => {
                  const slug = e.target.value;
                  setActiveSport(slug);
                  const seasons = archivesIndex.by_sport?.[slug] || [];
                  setActiveSeason(seasons[0]?.season_slug || null);
                }}
                className={`h-9 text-sm rounded border px-3 min-w-[160px] ${inputCls}`}
                data-testid="historical-sport-select">
                {sportOptions.map(s => (
                  <option key={s.slug} value={s.slug}>
                    {s.label}{s.hasArchives ? '' : ' (no data)'}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <span className={`text-[10px] uppercase tracking-wide font-semibold ${muted}`}>Season</span>
              <select value={activeSeason || ''}
                onChange={e => setActiveSeason(e.target.value)}
                className={`h-9 text-sm rounded border px-3 min-w-[180px] ${inputCls}`}
                data-testid="historical-season-select">
                {seasonsForActiveSport.length === 0 && <option value="">— no archives —</option>}
                {seasonsForActiveSport.map(s => (
                  <option key={s.season_slug} value={s.season_slug}>
                    {s.season_label}
                    {s.edited_by_ad ? ' · AD-edited' : (s.is_simulated ? ' · simulated' : '')}
                  </option>
                ))}
              </select>
            </div>
            {canEdit && archiveDoc && (
              <Button
                size="sm"
                onClick={pushArchiveToWp}
                disabled={pushingArchive}
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-9"
                data-testid="push-archive-wp-btn"
                title="Re-publish this archive's games to WordPress so the public site matches your OL edits."
              >
                {pushingArchive ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}
                Push to WordPress
              </Button>
            )}
          </div>
          {archivePushMsg && (
            <div className={`text-xs px-3 py-2 rounded-md ${archivePushMsg.type === 'ok' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`} data-testid="archive-push-flash">
              {archivePushMsg.text}
            </div>
          )}

          {/* Body — archive doc, or empty / loading state */}
          {archiveLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
          ) : !archiveDoc ? (
            <div className={`text-center py-10 text-sm ${muted}`} data-testid="archive-empty">
              No archives available for <strong>{activeSport}</strong> on this organization yet.
            </div>
          ) : (
            <HistoricalArchiveView
              doc={archiveDoc}
              isDark={isDark}
              text={text}
              muted={muted}
              inputCls={inputCls}
              canEdit={canEdit}
              rowState={rowState}
              onChange={(token, mutator, patch) => {
                mutateTeam(token, mutator);
                scheduleTeamPatch(token, patch);
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Record Result Dialog (unchanged) ───────────────────────────── */}
      <Dialog open={showRecordDialog} onOpenChange={setShowRecordDialog}>
        <DialogContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
          <DialogHeader><DialogTitle className={text}>Record Game Result</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className={muted}>Select Game</Label>
              <select className={`w-full mt-1 h-10 rounded-md px-3 border ${isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300 text-slate-900'}`}
                value={selectedGame?.id || ''}
                onChange={e => setSelectedGame(acceptedGames.find(g => g.id === e.target.value))}
                data-testid="game-select">
                <option value="">Choose a game...</option>
                {acceptedGames.map(g => (
                  <option key={g.id} value={g.id}>
                    {g.team_name || 'Team'} vs {g.opponent?.team_name || 'Opponent'} — {g.game_datetime?.substring(0, 10)}
                  </option>
                ))}
              </select>
            </div>
            {selectedGame && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={muted}>Home Score</Label>
                  <Input type="number" min="0" value={scoreForm.home_score}
                    onChange={e => setScoreForm({ ...scoreForm, home_score: e.target.value })}
                    className={inputCls} data-testid="home-score-input" />
                </div>
                <div>
                  <Label className={muted}>Away Score</Label>
                  <Input type="number" min="0" value={scoreForm.away_score}
                    onChange={e => setScoreForm({ ...scoreForm, away_score: e.target.value })}
                    className={inputCls} data-testid="away-score-input" />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowRecordDialog(false)} className={muted}>Cancel</Button>
              <Button onClick={async () => {
                if (!selectedGame) return;
                setSaving(true);
                try {
                  await axios.post(`${API_URL}/api/stats/games/${selectedGame.id}/result`, {
                    home_score: parseInt(scoreForm.home_score) || 0,
                    away_score: parseInt(scoreForm.away_score) || 0,
                  });
                  setShowRecordDialog(false);
                  setScoreForm({ home_score: '', away_score: '' });
                  fetchStandings(stdSport, stdSeason);
                } catch (err) { console.error('Failed to record result:', err); }
                setSaving(false);
              }} disabled={!selectedGame || saving}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-result-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════════
 * HistoricalArchiveView — renders one season_archives doc with:
 *   - Big total record card (147-42 / 77.9%) + HS / Private / Nationals
 *     + Boys / Girls split row
 *   - Per-team cards with editable record / splits / pf
 * Every Input is inline-editable; parent component handles debounced PATCH.
 * ════════════════════════════════════════════════════════════════════════ */
function HistoricalArchiveView({ doc, isDark, text, muted, inputCls, canEdit, rowState, onChange }) {
  const teams = doc.teams || {};
  const aggs = doc.aggregates || {};
  const aggT = aggs.team || {};
  const aggB = aggs.boys || {};
  const aggG = aggs.girls || {};

  const heroBg = isDark ? 'bg-slate-900 border-slate-700' : 'bg-amber-50/40 border-amber-200/60';
  const colorBoys = isDark ? 'text-blue-400' : 'text-blue-600';
  const colorGirls = isDark ? 'text-pink-400' : 'text-pink-600';

  // Order teams: VB, VG, JVB, JVG, MSB, MSG; anything else after.
  const orderedTokens = Object.keys(teams).sort((a, b) => {
    const ia = TOKEN_ORDER.indexOf(a); const ib = TOKEN_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

  return (
    <div className="space-y-5" data-testid="historical-archive-view">
      {/* Provenance ribbon */}
      <div className={`flex items-center gap-2 text-[11px] ${muted}`}>
        <span>Source: <strong className={text}>{doc._ol_source || 'archive'}</strong></span>
        {doc.edited_by_ad && (
          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 border text-[10px]">AD-edited (canonical)</Badge>
        )}
        {doc._ol_source === 'simulated' && !doc.edited_by_ad && (
          <Badge className="bg-purple-500/15 text-purple-400 border-purple-500/30 border text-[10px]">Simulated placeholder — edit to make canonical</Badge>
        )}
      </div>

      {/* Total record hero */}
      <div className={`rounded-lg border p-5 ${heroBg}`} data-testid="archive-total-hero">
        <div className="flex flex-col items-center text-center">
          <div className="flex items-baseline gap-3">
            <span className="text-4xl sm:text-5xl font-extrabold text-amber-500" data-testid="archive-total-record">
              {fmtRecord(aggT.record)}
            </span>
            <span className={`text-base sm:text-lg font-semibold ${text}`}>{pct(aggT.record)}</span>
          </div>
          <div className={`mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs ${muted}`}>
            {SPLIT_KEYS.map(k => (
              <span key={k}><strong className={text}>{SPLIT_LABEL[k]}:</strong> {fmtRecord(aggT.splits?.[k])}</span>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs">
            <span><strong className={colorBoys}>Boys:</strong> <span className={text}>{fmtRecord(aggB.record)} ({pct(aggB.record)})</span></span>
            <span><strong className={colorGirls}>Girls:</strong> <span className={text}>{fmtRecord(aggG.record)} ({pct(aggG.record)})</span></span>
          </div>
        </div>
      </div>

      {/* Per-team cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="archive-team-grid">
        {orderedTokens.map(token => {
          const t = teams[token] || {};
          const state = rowState[token] || 'idle';
          return (
            <TeamArchiveCard
              key={token}
              token={token}
              team={t}
              isDark={isDark}
              muted={muted}
              text={text}
              inputCls={inputCls}
              canEdit={canEdit}
              saveState={state}
              onPatch={(mutator, patch) => onChange(token, mutator, patch)}
            />
          );
        })}
      </div>
    </div>
  );
}

function TeamArchiveCard({ token, team, isDark, muted, text, inputCls, canEdit, saveState, onPatch }) {
  const rec = team.record || { w: 0, l: 0 };
  const splits = team.splits || {};
  const pf = team.pf ?? 0;

  const headerColor =
    token.endsWith('B') ? (isDark ? 'border-blue-400/60' : 'border-blue-400') :
                          (isDark ? 'border-pink-400/60' : 'border-pink-400');

  const setRecord = (key, val) => {
    const num = Math.max(0, parseInt(val || '0', 10) || 0);
    onPatch(
      (prev) => ({ ...prev, record: { ...(prev.record || {}), [key]: num } }),
      { record: { [key]: num } },
    );
  };
  const setSplit = (skey, rkey, val) => {
    const num = Math.max(0, parseInt(val || '0', 10) || 0);
    onPatch(
      (prev) => ({ ...prev, splits: { ...(prev.splits || {}), [skey]: { ...(prev.splits?.[skey] || {}), [rkey]: num } } }),
      { splits: { [skey]: { [rkey]: num } } },
    );
  };
  const setPF = (val) => {
    const num = Math.max(0, parseInt(val || '0', 10) || 0);
    onPatch((prev) => ({ ...prev, pf: num }), { pf: num });
  };
  const setPA = (val) => {
    const num = Math.max(0, parseInt(val || '0', 10) || 0);
    onPatch((prev) => ({ ...prev, pa: num }), { pa: num });
  };

  const tile = isDark ? 'bg-slate-800/40' : 'bg-white';

  return (
    <div className={`rounded-lg ${tile} border-t-4 ${headerColor} shadow-sm p-4`} data-testid={`archive-team-card-${token}`}>
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-baseline gap-2">
          <h4 className={`text-sm font-semibold ${text}`}>{team.name || TOKEN_LABEL[token] || token}</h4>
          <Badge variant="outline" className="text-[10px] border-slate-500/40">{token}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xl font-extrabold text-amber-500" data-testid={`archive-team-record-${token}`}>
            {rec.w}-{rec.l}
          </span>
          <span className={`text-xs ${muted}`}>{pct(rec)}</span>
          {saveState === 'saving' && <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />}
          {saveState === 'saved'  && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />}
          {saveState === 'error'  && <AlertCircle className="h-3.5 w-3.5 text-red-500" title="Save failed" />}
        </div>
      </div>

      {/* Editable record (W/L) */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <NumberField label="Wins" value={rec.w} onChange={v => setRecord('w', v)} disabled={!canEdit}
          inputCls={inputCls} muted={muted} testId={`archive-${token}-w`} />
        <NumberField label="Losses" value={rec.l} onChange={v => setRecord('l', v)} disabled={!canEdit}
          inputCls={inputCls} muted={muted} testId={`archive-${token}-l`} />
      </div>

      {/* Splits */}
      <div className={`text-[10px] uppercase tracking-wide font-semibold ${muted} mb-1.5`}>Splits (W-L)</div>
      <div className="space-y-1.5 mb-3">
        {SPLIT_KEYS.map(sk => {
          const s = splits[sk] || { w: 0, l: 0 };
          return (
            <div key={sk} className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
              <span className={`text-xs ${text}`}>{SPLIT_LABEL[sk]}</span>
              <Input type="number" min="0" value={s.w} disabled={!canEdit}
                onChange={e => setSplit(sk, 'w', e.target.value)}
                className={`h-7 w-14 text-xs text-center ${inputCls}`}
                data-testid={`archive-${token}-${sk}-w`} />
              <Input type="number" min="0" value={s.l} disabled={!canEdit}
                onChange={e => setSplit(sk, 'l', e.target.value)}
                className={`h-7 w-14 text-xs text-center ${inputCls}`}
                data-testid={`archive-${token}-${sk}-l`} />
            </div>
          );
        })}
      </div>

      {/* Points For / Against */}
      <div className="grid grid-cols-2 gap-2">
        <NumberField label="PF (Points For)" value={pf} onChange={setPF} disabled={!canEdit}
          inputCls={inputCls} muted={muted} testId={`archive-${token}-pf`} />
        <NumberField label="PA (Points Against)" value={team.pa ?? 0} onChange={setPA} disabled={!canEdit}
          inputCls={inputCls} muted={muted} testId={`archive-${token}-pa`} />
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, disabled, inputCls, muted, testId }) {
  return (
    <div>
      <div className={`text-[10px] uppercase tracking-wide font-semibold ${muted} mb-0.5`}>{label}</div>
      <Input type="number" min="0" value={value ?? 0} disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className={`h-7 text-xs ${inputCls}`}
        data-testid={testId} />
    </div>
  );
}
